import * as React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { useParams } from 'react-router-dom'

import * as LodashFP from 'lodash/fp'
import * as Lodash from 'lodash'

import { IFilesService } from '../../interfaces/services/files'

import {
  Box,
  Typography,
  Grid,
  Tooltip,
  Icon,
  Button,
  styled,
} from '@design/pt-ui'
import { toast } from 'react-toastify'
import Comment from '../../components/Comment'

import { Result, IAppError, resultSuccess } from '../../interfaces/base'
import { Model as CreativeViewState } from '../../interfaces/pages/creative'
import { IBlueprintService } from '../../interfaces/services/blueprints'
import {
  FormValue,
  IFormValidation,
  formValidation,
  FormBlueprint,
  FormChangeHandler,
} from '../../interfaces/components/dynamic-form'
import {
  Creative,
  CreativeAction,
  CreativeId,
  creativeHasTags,
} from '../../interfaces/entities/creative'
import { FormatsetId } from '../../interfaces/entities/formatset'
import { ICreativeService } from '../../interfaces/services/creatives'
import { useDirtySanchez } from '../../components/DirtySanchez'

import { State } from '../../model'
import * as actions from '../../model/creative/actions'

import Header from './Header'
import CreativePreview from './CreativePreview'
import Loader from '../../components/Loader'

import * as DynamicFormHelpers from '../../lib/dynamic-form/helpers'
import { ChangeCallback as LiveFormChangeCallback } from '../../lib/dynamic-form/LiveForm'
import LiveForm from '../../lib/dynamic-form/LiveForm'

import { User } from '../../interfaces/entities/user'
import DI from '../../app/di'
import { arrayPathFlobulator, forEach } from '../../utils/object'
import ValidationInfo from '../../components/ValidationInfo'
import CreativeActionButtons from '../../components/CreativeActionButtons'
import { translateWithUserLocale } from '../../utils/i18n'
import Disclaimer from './Disclaimer'
import AiInfoModal from '../../components/AiInfoModal'
import CreativePreviewNative from './CreativePreviewNative'

const HIDE_ERRORS_FOR_UNSAVED = true

const HeaderRow = styled('div')({
  display: 'flex',
  flexFlow: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
})

interface IProps {
  action: string | null
  user: User
  page: CreativeViewState

  getCreative: (id: string) => any
  getCreativeComplete: (result: Result<Creative, IAppError>) => any
  doCreativeAction: (creative: Creative, action: CreativeAction) => void
  setCreativeComment: (cretaiveId: CreativeId, comment: string) => any
  leaveCreativePage: () => any
  initializer: () => Promise<{
    value: FormValue
    blueprint: FormBlueprint
    validation: IFormValidation
    changeHandler: FormChangeHandler
  }>
}
interface IState {
  loading: boolean
  value: FormValue
  touched: boolean
  validation: IFormValidation
  blueprint: FormBlueprint | null
  changeHandler: FormChangeHandler | null
  saving: boolean
  isValidationDone: boolean
  creativeName: string
  dirty: boolean
  error: string | null
  canSave: boolean
}

const Main = (props: IProps) => {
  const { id } = useParams()
  const getInitialState = (props: IProps): IState => {
    return {
      loading: false,
      dirty: false,
      value: {},
      validation: formValidation(),
      touched: false,
      blueprint: null,
      changeHandler: null,
      saving: false,
      isValidationDone: false,
      creativeName: '',
      error: null,
      canSave: false,
    }
  }
  const { clean, defile } = useDirtySanchez()

  const [state, setState] = React.useState(getInitialState(props))

  const { leaveCreativePage } = props

  const creativeService = DI.get('CreativeService') as ICreativeService

  const handleComment = (comment: string) => {
    if (comment !== null && props.page.creative !== null) {
      props.setCreativeComment(props.page.creative.id, comment)
    }
  }

  React.useEffect(() => {
    loadCreative()
    return () => {
      leaveCreativePage()
      clean('creative-details')
    }
  }, [clean, leaveCreativePage]) // eslint-disable-line react-hooks/exhaustive-deps

  const getInitializer =
    (formatsetId: FormatsetId, type: string, creativeId: CreativeId) =>
    async () => {
      const blueprintService = DI.get('BlueprintService') as IBlueprintService

      const params = {
        formatsetId: formatsetId,
        type: type,
        creativeId: creativeId,
      }

      const response = await blueprintService.initCreativeForm(params)

      if (response.type === 'success') {
        const { validation, value, blueprint } = response.payload
        setState({
          ...state,
          value,
          blueprint,
          canSave: validation.validUntil === 'end',
          validation: response.payload.validation,
          isValidationDone: true,
        })
        return { ...response.payload }
      }

      throw response.payload
    }

  const changeHandler: () => LiveFormChangeCallback =
    () =>
    ({ value, validation }) => {
      setState({
        ...state,
        dirty: true,
        touched: true,
        value,
        canSave: validation.validUntil === 'end',
        validation,
      })
      defile('creative-details')
    }

  const renderForms = () => {
    const hasBeenSaved =
      props.page.creative != null
        ? creativeHasTags(props.page.creative, ['saved'])
        : null
    const filesService = DI.get('FilesService') as IFilesService
    const header = (
      <HeaderRow>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography variant="h6">
            {translateWithUserLocale('creative_details.creative_definition')}
          </Typography>
          <AiInfoModal />
        </Box>
        {HIDE_ERRORS_FOR_UNSAVED && !hasBeenSaved && !state.touched ? null : (
          <ValidationInfo
            blueprint={state.blueprint}
            validation={state.validation}
          />
        )}
        {props.page.creative?.project?.formatsets[0].type === 'facebook' && (
          <a
            href={filesService.getExcel(props.page.creative?.id)}
            style={{ marginLeft: 'auto' }}>
            <Button
              variant="outlined"
              sx={{ color: 'text.primary', borderColor: 'text.primary' }}>
              {translateWithUserLocale('generate_spec_file')}
            </Button>
          </a>
        )}
      </HeaderRow>
    )
    if (
      props.page.creative &&
      props.page.creative.status.name !== 'deleted' &&
      props.page.creative.project
    ) {
      const liveFormProps = {
        label: header,
        autoAddToEmptyArrays: true,
        initializer: getInitializer(
          props.page.creative.project.formatsets[0].id,
          props.page.creative.type.name,
          props.page.creative.id,
        ),
        colorizeNesting: true,
        onChange: changeHandler(),
        hideErrorsUntilTouched: HIDE_ERRORS_FOR_UNSAVED ? !hasBeenSaved : false,
        locked: !creativeService.canEdit(props.page.creative),
        platform: props.page.creative.project.formatsets[0].type,
        creative: props.page.creative,
      }
      return (
        <Box>
          {<LiveForm {...liveFormProps} creativeId={props.page.creative.id} />}
        </Box>
      )
    }
    return null
  }

  const loadCreative = () => {
    if (id) {
      props.getCreative(id)
    }
  }

  const handleSave = async (state: IState, finalize: boolean) => {
    try {
      if (props.page.creative && props.page.creative.project) {
        setState((state) => ({ ...state, saving: true }))

        const params = {
          id: props.page.creative.id,
          formatsetId: props.page.creative.project.formatsets[0].id,
          type: props.page.creative.type.name,
          data: state.value,
          name: state.creativeName.length
            ? state.creativeName
            : props.page.creative.name,
          blueprint: state.blueprint as FormBlueprint,
        }

        const response = finalize
          ? await creativeService.finalizeCreative(params)
          : await creativeService.updateCreative(params)

        if (response.type === 'success') {
          clean('creative-details')
          setState((state) => ({
            ...state,
            saving: false,
            dirty: false,
            error: null,
          }))
          if (finalize) {
            const history = DI.get('history')
            const url = `/project/${props.page.creative.project.id}`
            history.push(url)
            toast.success(
              translateWithUserLocale('notifications.creative_finalized', {
                name: props.page.creative.name,
              }),
            )
          }
        } else {
          if (response.payload.message === null && finalize) {
            setState((state) => ({ ...state, saving: false, error: null }))
          } else {
            setState((state) => ({
              ...state,
              saving: false,
              error: response.payload.message,
            }))
            toast.error(response.payload.message, { autoClose: 5000 })
          }
        }
      }
    } catch (e) {
      setState((state) => ({
        ...state,
        saving: false,
        error:
          translateWithUserLocale('error.something_went_wrong_apologetic') +
          '.',
      }))
    }
  }

  const handleFinalize = async (state: IState) => {
    await handleSave(state, true)
  }

  const handleNameChange = async (value: string) => {
    const newState = { ...state, creativeName: value }
    setState(newState)

    await handleSave(newState, false)
    if (props.page.creative) {
      const newCreative = { ...props.page.creative, name: value }
      props.getCreativeComplete(resultSuccess(newCreative))
    }
  }

  const handleValidate = async () => {
    setState({
      ...state,
      saving: true,
    })

    try {
      const creativeService = DI.get('CreativeService') as ICreativeService

      if (props.page.creative && props.page.creative.project) {
        const params = {
          formatsetId: props.page.creative.project.formatsets[0].id,
          id: props.page.creative.id,
          type: props.page.creative.type.name,
          data: state.value,
          name: state.creativeName,
          blueprint: state.blueprint as FormBlueprint,
        }

        const response = await creativeService.validateCreative(params)

        if (response.type === 'success') {
          setState({ ...state, saving: false, isValidationDone: true })
        } else {
          if (response.payload.message === null) {
            setState({
              ...state,
              saving: false,
              isValidationDone: true,
              error: null,
            })
          } else {
            setState({
              ...state,
              saving: false,
              isValidationDone: true,
              error: response.payload.message,
            })
            toast.error(response.payload.message, { autoClose: 5000 })
          }
        }
      }
    } catch (e) {
      setState({
        ...state,
        saving: false,
        error: translateWithUserLocale('error.something_went_wrong_apologetic'),
      })
    }
  }

  const creative = props.page.creative

  if (props.page.loading || state.loading) {
    return (
      <Loader message={translateWithUserLocale('loaders.loading_creative')} />
    )
  }

  const initialCreative = state.blueprint
    ? DynamicFormHelpers.initialize(state.blueprint)
    : {}

  const mergedCreative = creative
    ? {
        ...creative,
        data: {
          ...creative.data,
          ...state.value,
        },
      }
    : null

  const unnestedPaths = state.blueprint
    ? DynamicFormHelpers.filterBlueprintPaths(state.blueprint, (blueprint) => {
        return blueprint.type !== 'fieldset' && blueprint.type !== 'list'
      })
    : []

  const safeData = LodashFP.cloneDeep(
    LodashFP.getOr({}, 'data', mergedCreative),
  )
  if (state.validation.validUntil !== 'end') {
    forEach(state.validation.fields, (path, e) => {
      for (const msg of e) {
        if (
          msg.status === 'error' &&
          unnestedPaths.includes(arrayPathFlobulator(path))
        ) {
          const initialValue = LodashFP.getOr(null, path, initialCreative)
          Lodash.set(safeData, path, initialValue)
        }
      }
    })
  }

  return (
    <Grid spacing={1} container={true}>
      {props.page.loading || state.loading || state.saving ? (
        <Loader
          variant="overlay"
          message={translateWithUserLocale('loaders.loading_creative')}
        />
      ) : null}
      <Grid
        size={{
          xs: 12,
          sm: 12,
          md: 12,
        }}>
        <Header
          dirty={state.dirty}
          creative={
            props.user.groups.includes('client') && props.page.creative !== null
              ? {
                  ...props.page.creative,
                  possibleActions: props.page.creative.possibleActions.filter(
                    (action) => action.name !== 'send',
                  ),
                }
              : props.page.creative
          }
          creativeName={state.creativeName}
          doCreativeAction={props.doCreativeAction}
          setCreativeComment={props.setCreativeComment}
          onSave={() => handleSave(state, false)}
          onFinalize={() => handleFinalize(state)}
          onValidate={handleValidate}
          onNameChanged={(name: string) => handleNameChange(name)}
          validation={state.validation}
          validating={state.saving}
          error={state.error}
          isValidationDone={state.isValidationDone}
          blueprint={state.blueprint}
        />
      </Grid>
      {creative ? (
        <Grid
          sx={{
            px: '12px !important',
          }}
          size={{
            xs: 12,
            sm: 12,
            md: 12,
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingBottom: '10px',
            }}>
            <Typography variant="body2">
              {translateWithUserLocale('comment')}
            </Typography>

            <Tooltip
              title={
                mergedCreative?.project?.formatsets[0].type === 'facebook'
                  ? translateWithUserLocale('comment_info_fb')
                  : translateWithUserLocale('comment_info_pa')
              }
              placement="top">
              <Icon sx={{ ml: '10px', color: 'grey' }}>info</Icon>
            </Tooltip>
          </Box>
          <Comment
            comment={creative.comment}
            disabled={!creativeService.canEdit(creative)}
            onSave={
              creativeService.canEdit(creative) ? handleComment : undefined
            }
            editIconSize="small"
            showMessageIcon={false}
          />
        </Grid>
      ) : null}
      <Grid
        sx={{
          pl: '12px',
          pt: '6px',
        }}
        size={{
          sm: 12,
          lg: 7,
        }}>
        {renderForms()}
        <Disclaimer />
        <div style={{ float: 'right' }}>
          {state.isValidationDone && (
            <CreativeActionButtons
              creative={creative}
              validating={state.saving}
              validation={state.validation}
              isValidationDone={state.isValidationDone}
              dirty={state.dirty}
              blueprint={state.blueprint}
              doCreativeAction={props.doCreativeAction}
              onSave={() => handleSave(state, false)}
              onFinalize={() => handleFinalize(state)}
              onValidate={handleValidate}
            />
          )}
        </div>
      </Grid>
      {id ? (
        <Grid
          sx={{
            pr: '12px',
            pl: '6px',
          }}
          size={{
            sm: 12,
            lg: 5,
          }}>
          {mergedCreative &&
          mergedCreative.status.name !== 'deleted' &&
          mergedCreative.project?.formatsets[0].type === 'facebook' &&
          state.isValidationDone ? (
            <CreativePreview
              creative={{ ...mergedCreative, data: safeData }}
              disabled={false}
            />
          ) : (
            mergedCreative &&
            mergedCreative.status.name !== 'deleted' &&
            mergedCreative.project?.formatsets[0].type ===
              'performance_advertising' && (
              <CreativePreviewNative creative={mergedCreative} />
            )
          )}
        </Grid>
      ) : null}
    </Grid>
  )
}

const mapStateToProps = (state: State) => {
  return {
    page: state.creative,
    user: state.user,
  }
}
const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    getCreative: (creativeId: string) =>
      dispatch(actions.getCreative(creativeId)),
    getCreativeComplete: (result: Result<Creative, IAppError>) =>
      dispatch(actions.getCreativeComplete(result)),
    doCreativeAction: (creative: Creative, action: CreativeAction) =>
      dispatch(actions.doCreativeAction(creative, action)),
    setCreativeComment: (creativeId: CreativeId, comment: string) =>
      dispatch(actions.setCreativeComment(creativeId, comment)),
    leaveCreativePage: () => dispatch(actions.leaveCreativePage()),
  }
}
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Main as any) as React.ComponentType<any>
